import { printShapes } from "./print-layout";
import type { DraftBlock, DraftResult, PointTuple } from "./types";

/**
 * Laying pieces out on the cloth (a marker): every piece turned so its grain
 * runs along the selvedge, then packed as tight as the cloth allows.
 *
 * Fabric coordinates, in mm: x runs along the length of the cloth from where
 * cutting starts; y runs across it from the fold (folded cloth) or from one
 * selvedge (open cloth). Folded cloth is laid selvedge to selvedge, so it is
 * half as wide and every piece laid on it is cut twice, as a mirrored pair.
 * Pieces too wide for the folded cloth go after it, where the cloth is opened
 * out to its full width.
 */
export interface FabricSettings {
  /** Selvedge to selvedge, in mm. */
  width: number;
  /** Length of cloth to hand, in mm; undefined for as long as it takes. */
  length?: number;
  /** Folded in half along its length, to cut pairs (fronts, backs, sleeves) through both layers. */
  folded: boolean;
  /** One-way cloth (nap, pile or a one-way print): every piece's top points the same way. */
  nap: boolean;
  /** Clear cloth left between pieces, in mm. */
  gap: number;
  /** Grid the pieces are packed on, in mm. Finer packs tighter and runs slower. */
  cell?: number;
  /**
   * Laying out the paper pattern, not the cloth: each piece once, face up, in
   * any quarter turn, packed into `width` (a roll of paper). Fold, nap and
   * cut quantities do not apply.
   */
  paper?: boolean;
}

/** A piece to lay out: what it covers in its own coordinates, and how it must be cut. */
export interface FabricPiece {
  key: string;
  /** Polygons covered (outline, seam allowance, inlay), in the piece's own coordinates. */
  shapes: PointTuple[][];
  grainDirection: DraftBlock["grainDirection"];
  cutQuantity: number;
  /** The ends of the edge laid on the fold, when the piece is cut on the fold. */
  fold?: [PointTuple, PointTuple];
}

export interface FabricPlacement {
  key: string;
  /** Which of the piece's placements this is, from 0. */
  copy: number;
  /** Piece coordinates to fabric coordinates, as an SVG matrix: x' = a·x + c·y + e, y' = b·x + d·y + f. */
  matrix: [number, number, number, number, number, number];
  /** Laid face down: the mirror image of the piece as drafted. */
  mirrored: boolean;
  /** Turn applied before any mirroring, in degrees anticlockwise as drawn (y down). */
  rotation: number;
  /** Laid with its fold edge on the fold of the cloth. */
  onFold: boolean;
  /** Cut through both layers though the piece is needed once more only: one of the pair is spare. */
  spare: boolean;
  /** On the folded cloth, or on the open cloth after it (pieces too wide for the fold). */
  section: "folded" | "open";
  /** Laid as the whole piece, opened out about its fold edge. */
  unfolded: boolean;
  /** Pieces cut by this placement: two on folded cloth, one on open cloth. */
  cuts: number;
  /** The space it takes on the cloth. */
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface FabricLayout {
  settings: FabricSettings;
  /** Usable width across the cloth: half the width when folded. */
  across: number;
  /** Length of cloth the layout takes, in mm. */
  length: number;
  /** Where folded cloth is opened out for the pieces too wide for it; the whole length when nothing needs it. */
  foldedLength: number;
  placements: FabricPlacement[];
  /** Placements that could not go on the cloth: too wide for it, even opened out, in every turn allowed. */
  unplaced: { key: string; copy: number; reason: "too-wide" }[];
  /** Share of the cloth used that the pieces cover. */
  efficiency: number;
  /** Everything placed, within the length to hand. */
  fits: boolean;
}

/** The pieces of a draft as fabric pieces, keyed by block id. */
export function fabricPieces(draft: Pick<DraftResult, "edges" | "seamAllowances" | "inlays">, blocks: DraftBlock[]): FabricPiece[] {
  return blocks.map((block) => {
    const shapes = printShapes({ ...block, printOffset: [0, 0], printRotation: 0 }, draft);
    const foldEdge = block.cutOnFold ? draft.edges.find((edge) => edge.id === block.cutOnFold) : undefined;
    const fold: [PointTuple, PointTuple] | undefined = foldEdge && foldEdge.segments.length > 0 ? [foldEdge.segments[0].start, foldEdge.segments[foldEdge.segments.length - 1].end] : undefined;
    return { key: block.id, shapes, grainDirection: block.grainDirection, cutQuantity: block.cutQuantity, fold };
  });
}

type Matrix = [number, number, number, number, number, number];
const apply = (m: Matrix, [x, y]: PointTuple): PointTuple => [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]];
/** Reflection in the line through `a` and `b`. */
function reflection([ax, ay]: PointTuple, [bx, by]: PointTuple): Matrix {
  const length = Math.hypot(bx - ax, by - ay) || 1;
  const [ux, uy] = [(bx - ax) / length, (by - ay) / length];
  const [a, b, d] = [2 * ux * ux - 1, 2 * ux * uy, 2 * uy * uy - 1];
  return [a, b, b, d, ax - a * ax - b * ay, ay - b * ax - d * ay];
}

/** A row interval [from, to), in cells. */
type Span = [number, number];

/** A piece in one orientation, rasterised: the cells it covers, column by column, and the halo it keeps clear. */
interface Footprint {
  matrix: Matrix;
  rotation: number;
  mirrored: boolean;
  columns: number;
  rows: number;
  core: Span[][];
  /** Core grown by the gap, in columns offset by `halo` to the left. */
  clear: Span[][];
  halo: number;
  area: number;
}

function mergeSpans(spans: Span[]): Span[] {
  const sorted = spans.filter(([a, b]) => b > a).sort((p, q) => p[0] - q[0]);
  const merged: Span[] = [];
  for (const span of sorted) {
    const last = merged[merged.length - 1];
    if (last && span[0] <= last[1]) last[1] = Math.max(last[1], span[1]);
    else merged.push([span[0], span[1]]);
  }
  return merged;
}

/** Where the vertical line at `x` is inside the polygon, as y ranges. */
function verticalSpans(polygon: PointTuple[], x: number): [number, number][] {
  const crossings: number[] = [];
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i], [xj, yj] = polygon[j];
    if ((xi > x) !== (xj > x)) crossings.push(yi + ((x - xi) * (yj - yi)) / (xj - xi));
  }
  crossings.sort((a, b) => a - b);
  const spans: [number, number][] = [];
  for (let i = 0; i + 1 < crossings.length; i += 2) spans.push([crossings[i], crossings[i + 1]]);
  return spans;
}

function rasterise(shapes: PointTuple[][], matrix: Matrix, rotation: number, mirrored: boolean, cell: number, gap: number): Footprint {
  const placed = shapes.map((shape) => shape.map((point) => apply(matrix, point)));
  const xs = placed.flat().map(([x]) => x), ys = placed.flat().map(([, y]) => y);
  const minX = Math.min(...xs), minY = Math.min(...ys);
  // Move the piece so its corner sits at the origin of its own grid.
  const shifted: Matrix = [matrix[0], matrix[1], matrix[2], matrix[3], matrix[4] - minX, matrix[5] - minY];
  const local = placed.map((shape) => shape.map(([x, y]): PointTuple => [x - minX, y - minY]));
  const columns = Math.max(1, Math.ceil((Math.max(...xs) - minX) / cell));
  const rows = Math.max(1, Math.ceil((Math.max(...ys) - minY) / cell));
  const core: Span[][] = [];
  let area = 0;
  for (let column = 0; column < columns; column++) {
    // Three lines through the column, so a narrow point is not missed between them.
    const lines = [column * cell + 0.01, (column + 0.5) * cell, (column + 1) * cell - 0.01];
    const spans = mergeSpans(local.flatMap((shape) => lines.flatMap((x) => verticalSpans(shape, x))).map(([from, to]): Span => [Math.floor(from / cell), Math.ceil(to / cell)]));
    core.push(spans);
    area += local.length > 0 ? mergeSpans(local.flatMap((shape) => verticalSpans(shape, (column + 0.5) * cell))).reduce((sum, [from, to]) => sum + (to - from), 0) * cell : 0;
  }
  const halo = Math.ceil(gap / cell);
  const clear: Span[][] = Array.from({ length: columns + 2 * halo }, (_, index) => {
    const near = core.slice(Math.max(0, index - 2 * halo), Math.min(columns, index + 1)).flat();
    return mergeSpans(near.map(([from, to]): Span => [from - halo, to + halo]));
  });
  return { matrix: shifted, rotation, mirrored, columns, rows, core, clear, halo, area };
}

/** The turns that keep a piece's grain along the selvedge (x): with nap only the one, without nap its reverse too. */
function grainTurns(grain: DraftBlock["grainDirection"], nap: boolean): number[] {
  // A piece's straight grain runs up and down it (y); turning it -90° lays that along x, the piece's top towards the start of the cloth.
  const base = grain === "horizontal" ? 0 : grain === "bias" ? -45 : -90;
  return nap ? [base] : [base, base + 180];
}

function orientationMatrix(rotation: number, mirrored: boolean): Matrix {
  const radians = (rotation * Math.PI) / 180;
  const [cos, sin] = [Math.cos(radians), Math.sin(radians)];
  // Rotate, then mirror across the length of the cloth (y → -y), which keeps the grain along it.
  const flip = mirrored ? -1 : 1;
  return [cos, sin * flip, -sin, cos * flip, 0, 0];
}

/** The cloth: for each column, the rows pieces already cover. */
class Cloth {
  private columns: Span[][] = [];
  constructor(readonly rows: number) {}

  private occupied(column: number): Span[] {
    return column >= 0 && column < this.columns.length ? this.columns[column] : [];
  }

  /** The lowest row at or after `start` where the footprint fits at `column`, or undefined if it reaches past the far edge. */
  fitAt(footprint: Footprint, column: number, start: number): number | undefined {
    const coreBottom = Math.max(...footprint.core.map((spans) => spans[spans.length - 1]?.[1] ?? 0));
    let row = start;
    for (;;) {
      if (row + coreBottom > this.rows) return undefined;
      let next = row;
      for (let index = 0; index < footprint.clear.length && next === row; index++) {
        const taken = this.occupied(column + index - footprint.halo);
        if (taken.length === 0) continue;
        for (const [from, to] of footprint.clear[index]) {
          const hit = taken.find(([a, b]) => a < to + row && b > from + row);
          if (hit) { next = Math.max(next, hit[1] - from); break; }
        }
      }
      if (next === row) return row;
      row = next;
    }
  }

  clone(): Cloth {
    const copy = new Cloth(this.rows);
    copy.columns = this.columns.map((spans) => spans.map(([from, to]): Span => [from, to]));
    return copy;
  }

  place(footprint: Footprint, column: number, row: number) {
    footprint.core.forEach((spans, index) => {
      const at = column + index;
      while (this.columns.length <= at) this.columns.push([]);
      this.columns[at] = mergeSpans([...this.columns[at], ...spans.map(([from, to]): Span => [from + row, to + row])]);
    });
  }
}

interface Job { piece: FabricPiece; copy: number; onFold: boolean; unfolded: boolean; spare: boolean; cuts: number; footprints: Footprint[] }

interface Placed { job: Job; footprint: Footprint; column: number; row: number }

/** Pieces packed onto one stretch of cloth so far. */
class Packer {
  cloth: Cloth;
  used = 0;
  placed: Placed[] = [];
  unplaced: Job[] = [];
  constructor(rows: number) { this.cloth = new Cloth(rows); }

  /** Where the job adds least to the length, ending by column `limit` when given. */
  private spot(job: Job, limit = Infinity) {
    let best: { footprint: Footprint; column: number; row: number; end: number } | undefined;
    for (const footprint of job.footprints) {
      if (footprint.rows > this.cloth.rows) continue;
      for (let column = 0; column + footprint.columns <= limit; column++) {
        const end = Math.max(this.used, column + footprint.columns);
        if (best && (end > best.end || (end === best.end && column >= best.column))) break;
        // A piece on the fold keeps its fold edge on the fold: row 0, nowhere else.
        const row = this.cloth.fitAt(footprint, column, 0);
        if (row === undefined || (job.onFold && row !== 0)) continue;
        best = { footprint, column, row, end };
        break;
      }
    }
    return best;
  }

  add(job: Job): boolean {
    const best = this.spot(job);
    if (!best) { this.unplaced.push(job); return false; }
    this.cloth.place(best.footprint, best.column, best.row);
    this.used = best.end;
    this.placed.push({ job, footprint: best.footprint, column: best.column, row: best.row });
    return true;
  }

  /** Places every job within the length already used, or none of them. */
  fillAll(jobs: Job[]): boolean {
    const cloth = this.cloth.clone();
    const placed: Placed[] = [];
    for (const job of jobs) {
      const best = this.spot(job, this.used);
      if (!best) { this.cloth = cloth; return false; }
      this.cloth.place(best.footprint, best.column, best.row);
      placed.push({ job, footprint: best.footprint, column: best.column, row: best.row });
    }
    this.placed.push(...placed);
    return true;
  }
}

/** One packing of the jobs in the order given: each takes the spot that lengthens the layout least. */
function pack(jobs: Job[], across: number, cell: number, packer = new Packer(Math.floor(across / cell))) {
  for (const job of jobs) packer.add(job);
  return packer;
}

/**
 * Lays the pieces out on the cloth. Each placement is tried in every turn its
 * grain allows (mirrored too, where that still cuts the right pieces) and takes
 * the spot that adds least to the length; a few orders of the pieces are tried
 * and the shortest layout kept.
 */
export function layoutFabric(pieces: FabricPiece[], requested: FabricSettings): FabricLayout {
  const settings = requested.paper ? { ...requested, folded: false, nap: false } : requested;
  const cell = settings.cell ?? 5;
  const across = settings.folded ? settings.width / 2 : settings.width;
  const fits = (rows: number) => (job: Job) => job.footprints.some((footprint) => footprint.rows <= rows);
  // On folded cloth, a piece too wide for the fold goes on the cloth opened out after it.
  const folded = settings.folded ? pieces.map((piece) => ({ piece, jobs: jobsFor(piece, settings, true, cell) })) : [];
  const onFold = folded.filter(({ jobs }) => jobs.every(fits(Math.floor(across / cell))));
  const opened = settings.folded ? folded.filter((entry) => !onFold.includes(entry)).map(({ piece }) => piece) : pieces;
  const openJobs = (piece: FabricPiece) => jobsFor(piece, settings, false, cell);
  const second = packOrders(opened.flatMap(openJobs), settings.width, cell);
  // The open cloth the wide pieces need leaves room beside them: fill it with
  // other pieces, single layer, wherever all of a piece's copies fit there.
  const area = ({ jobs }: { jobs: Job[] }) => jobs.reduce((sum, job) => sum + (job.footprints[0]?.area ?? 0) * job.cuts, 0);
  // Biggest first: they save the most folded cloth.
  const moved = new Set(opened.length === 0 ? [] : [...onFold].sort((a, b) => area(b) - area(a)).filter(({ piece }) => second.fillAll(openJobs(piece))));
  const rest = onFold.filter((entry) => !moved.has(entry));
  const first = packOrders(rest.flatMap(({ jobs }) => jobs), across, cell);

  const foldedLength = first.used * cell;
  // The open cloth starts a gap after the last folded piece.
  const openStart = first.placed.length > 0 && second.placed.length > 0 ? foldedLength + settings.gap : 0;
  const placements = [...first.placed.map((item) => ({ ...item, section: "folded" as const, start: 0 })), ...second.placed.map((item) => ({ ...item, section: settings.folded ? "open" as const : "folded" as const, start: openStart }))]
    .map(({ job, footprint, column, row, section, start }): FabricPlacement => {
      const [a, b, c, d, e, f] = footprint.matrix;
      const x = start + column * cell;
      return {
        key: job.piece.key, copy: job.copy, matrix: [a, b, c, d, e + x, f + row * cell], mirrored: footprint.mirrored, rotation: footprint.rotation,
        onFold: job.onFold, unfolded: job.unfolded, spare: job.spare, cuts: job.cuts, section: settings.folded ? section : "open",
        x, y: row * cell, width: footprint.columns * cell, height: footprint.rows * cell,
      };
    });
  const length = second.placed.length > 0 ? openStart + second.used * cell : foldedLength;
  const clothArea = settings.folded ? foldedLength * across + (length - foldedLength) * settings.width : length * across;
  const covered = [...first.placed, ...second.placed].reduce((sum, { footprint }) => sum + footprint.area, 0);
  const unplaced = [...first.unplaced, ...second.unplaced].map((job) => ({ key: job.piece.key, copy: job.copy, reason: "too-wide" as const }));
  return {
    settings, across, length, foldedLength: settings.folded && second.placed.length > 0 ? foldedLength : length, placements, unplaced,
    efficiency: clothArea > 0 ? covered / clothArea : 0,
    fits: unplaced.length === 0 && (settings.length === undefined || length <= settings.length),
  };
}

/** How a piece is cut on folded or open cloth: its placements, and the turns each may take. */
function jobsFor(piece: FabricPiece, settings: FabricSettings, folded: boolean, cell: number): Job[] {
  // Paper: the pattern piece itself, once, turned whichever way packs best.
  if (settings.paper) return [{ piece, copy: 0, onFold: false, unfolded: false, spare: false, cuts: 1, footprints: [0, 90, 180, 270].map((rotation) => rasterise(piece.shapes, orientationMatrix(rotation, false), rotation, false, cell, settings.gap)) }];
  const turns = grainTurns(piece.grainDirection, settings.nap);
  if (folded && piece.fold) {
    // On the fold: each placement cuts one whole piece, its fold edge along the fold at y = 0.
    const footprints = turns.flatMap((rotation) => [false, true].map((mirrored) => ({ rotation, mirrored, matrix: orientationMatrix(rotation, mirrored) })))
      .filter(({ matrix }) => {
        const [a, b] = piece.fold!.map((point) => apply(matrix, point));
        const all = piece.shapes.flat().map((point) => apply(matrix, point)[1]);
        return Math.abs(a[1] - b[1]) < 1 && Math.min(...all) > Math.min(a[1], b[1]) - 1;
      })
      .map(({ rotation, mirrored, matrix }) => rasterise(piece.shapes, matrix, rotation, mirrored, cell, settings.gap));
    if (footprints.length > 0) return Array.from({ length: piece.cutQuantity }, (_, copy) => ({ piece, copy, onFold: true, unfolded: false, spare: false, cuts: 1, footprints }));
    // Its fold edge runs across the grain, so it cannot lie on a lengthwise fold: lay it out whole instead.
  }
  // Off the fold, a fold piece is laid out whole: opened out about its fold edge.
  const unfolded = piece.fold !== undefined;
  const shapes = unfolded ? [...piece.shapes, ...piece.shapes.map((shape) => shape.map((point) => apply(reflection(...piece.fold!), point)))] : piece.shapes;
  const orient = (mirrors: boolean[]) => turns.flatMap((rotation) => mirrors.map((mirrored) => rasterise(shapes, orientationMatrix(rotation, mirrored), rotation, mirrored, cell, settings.gap)));
  if (folded) {
    // Both layers cut a mirrored pair, so the piece may lie either way up.
    const footprints = orient([false, true]);
    const placements = Math.ceil(piece.cutQuantity / 2);
    return Array.from({ length: placements }, (_, copy) => ({ piece, copy, onFold: false, unfolded, spare: piece.cutQuantity % 2 === 1 && copy === placements - 1, cuts: 2, footprints }));
  }
  // Single layer: a pair is one piece face up and one face down; a single (or whole, unfolded) piece face up.
  const pairs = piece.cutQuantity > 1 && !unfolded;
  const up = orient([false]), down = pairs ? orient([true]) : [];
  return Array.from({ length: piece.cutQuantity }, (_, copy) => ({ piece, copy, onFold: false, unfolded, spare: false, cuts: 1, footprints: pairs && copy % 2 === 1 ? down : up }));
}

/** Packs the jobs in a few orders and keeps the shortest layout. Fold pieces go first, so the fold is theirs. */
function packOrders(jobs: Job[], across: number, cell: number) {
  const size = (job: Job) => job.footprints[0] ?? { area: 0, columns: 0, rows: 0 };
  const orders: ((a: Job, b: Job) => number)[] = [
    (a, b) => size(b).area - size(a).area,
    (a, b) => Math.max(size(b).columns, size(b).rows) - Math.max(size(a).columns, size(a).rows),
    (a, b) => size(b).rows - size(a).rows || size(b).area - size(a).area,
  ];
  const results = orders.map((order) => pack([...jobs].sort((a, b) => Number(b.onFold) - Number(a.onFold) || order(a, b)), across, cell));
  return results.reduce((winner, result) => result.unplaced.length < winner.unplaced.length || (result.unplaced.length === winner.unplaced.length && result.used < winner.used) ? result : winner);
}
